import React, { useContext, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx'
import { RootStoreContext } from '../stores/root_store';

import { CollabProps } from 'stores/collab_store';
import { PagedResult } from 'utils/types';

import useUserIdentity from 'hooks/useUserIdentity';


type DedicatedCollabType = {
    job_1: string;
    job_2: string;
    collab: CollabProps;
};
export type CollabsContextType = {
    currentCollab: CollabProps | null
    setCurrentCollabKey: (collabKey: string) => void;
    collabs: CollabProps[];
    createCollab: (collab: CollabProps ) => Promise<CollabProps>;
    createCollabByDedicated: (workKey: string, collab: CollabProps) => Promise<DedicatedCollabType>;
    collabByKey: (key: string) => CollabProps | null;
    getCollab: (key: string) => CollabProps | null;
    //    fetchCollabs: (projectKey: string) => void;
    fetchCollab: (identityKey: string, collabKey: string) => void;
    fetchCollabsWith: (identityKey: string, mututalIdentityKey: string) => Promise<CollabProps[]>;
    getCollabsWith: (identityKey: string, mututalIdentityKey: string) => CollabProps[];

    fetchCollabsByProject: (identityKey: string, projectKey: string) => Promise<CollabProps[]>;
    getCollabsByProject: (identityKey: string, projectUuid: string, projectKey?: string) => CollabProps[];

    fetchCollabsByVariant: (identity: string, variant: string) => Promise<CollabProps[]>;
    getCollabsByVariant: (identity: string, variant: string) => CollabProps[];

    supportFetchCollabs: (workKey: string, lookup_identity: string, cursor?: string) => Promise<PagedResult<CollabProps> | undefined>;
    getCollabsCreatedBy: (identityKey: string) => CollabProps[];
}

export const CollabsContext = React.createContext<CollabsContextType>({
    currentCollab: null,
    setCurrentCollabKey: () => { throw "Not implemented" },
    createCollab: () => { throw "Not implemented" },
    createCollabByDedicated: () => { throw "Not implemented" },
    collabs: [] as CollabProps[],
    collabByKey: () => { throw "Not implemented" },
    getCollab: () => { throw "Not implemented" },
    fetchCollab: () => { throw "Not implemented" },
    //    fetchCollabs: () => { throw "Not implemented" },
    fetchCollabsWith: () => { throw "Not implemented" },
    fetchCollabsByProject: () => { throw "Not implemented" },
    getCollabsByProject: () => { throw "Not implemented" },
    getCollabsWith: () => { throw "Not implemented" },
    fetchCollabsByVariant: () => { throw "Not implemented" },
    getCollabsByVariant: () => { throw "Not implemented" },
    supportFetchCollabs: () => { throw "Not implemented" },
    getCollabsCreatedBy: () => { throw "Not implemented" },
});


export const CollabsProvider = observer(({ children }: { children: React.ReactNode }) => {
    const [currentCollab, setCurrentCollabState] = useState<CollabProps | null>(null);

    //    const [loadedCollabs, setLoadedCollabs] = useState(false);
    const { collabStore } = useContext(RootStoreContext);

    const { currentIdentity } = useUserIdentity();
    const [previousIdentity, setPreviousIdentity] = useState(currentIdentity?.uuid);

    /* clear the collabs if we change identity */
    useEffect(() => {
        if (currentIdentity.uuid != previousIdentity) {
            setPreviousIdentity(currentIdentity.uuid);
            runInAction(() => {
                collabStore.items = [];
            });
        }
    }, [currentIdentity.uuid, setPreviousIdentity, previousIdentity]);


    async function fetchCollab(identityKey: string, collabKey: string) {
        return collabStore.fetchCollab(identityKey, collabKey);
    }

    async function fetchCollabsByProject(identityUuid: string, projectUuid: string) {
        return collabStore.fetchCollabs(identityUuid, projectUuid).
            then(() => {
                return collabStore.items;
            });
    }

    const getCollabsByProject = (identityUuid: string, projectUuid: string, projectKey?: string) => {
        console.log("getCollabsByProject called with:", { identityUuid, projectUuid, projectKey });
        console.log("collabStore.items count:", collabStore.items.length);
        console.log("collabStore.items:", collabStore.items.map(c => ({
            uuid: c.uuid,
            project: c.project,
            cached_collaborators: c.cached_collaborators
        })));
        if (identityUuid === undefined)
            return [];
        const filtered = collabStore.items.filter((c) => {
            // Match on either uuid or key to handle old collabs that may have keys stored
            const projectMatches = c.project == projectUuid || (projectKey && c.project == projectKey);
            const collaboratorMatches = c.cached_collaborators.includes(identityUuid);
            console.log("Filtering collab:", {
                collabUuid: c.uuid,
                collabProject: c.project,
                projectUuid,
                projectKey,
                projectMatches,
                cached_collaborators: c.cached_collaborators,
                identityUuid,
                collaboratorMatches
            });
            return projectMatches && collaboratorMatches;
        });
        console.log("Filtered result count:", filtered.length);
        return filtered.sort((a, b) => {
            return a.date_created > b.date_created ? 1 : -1;
        });
    };

    const fetchCollabsByVariant = async (identity: string, variant: string) => {
        return collabStore.fetchCollabsByVariant(identity, variant).
            then(() => {
                return collabStore.items;
            });
    };

    const getCollabsByVariant = (identity: string, variant: string) => {
        return collabStore.getCollabsByVariant(identity, variant);
    };

    function collabByKey(key: string): CollabProps | null {
        return collabStore.items.find(c => c.key === key || c.uuid === key) || null;
    }

    async function setCurrentCollabKey(collabKey: string) {
        return collabStore.fetchCollab(currentIdentity.uuid, collabKey).
            then(() => {
                setCurrentCollabState(collabByKey(collabKey));
            });
    }

    async function createCollab(collab: CollabProps) {
        const c = await collabStore.createCollab(
            collab.project,
            collab.identity,
            collab.title,
            collab.description,
            collab.variant);
        return c;
    }

    async function createCollabByDedicated(workKey: string, collab: CollabProps) {
        return collabStore.createCollabByDedicated(
            workKey,
            collab.project,
            collab.identity,
            collab.title,
            collab.description).
            then((c) => {
                return c;
            });
    }

    const getCollabsWith = (identityKey: string, mututalIdentityKey: string) => {
        return collabStore.items.filter((c) =>
            c.cached_collaborators.includes(identityKey) && c.cached_collaborators.includes(mututalIdentityKey)
        );
    };

    async function fetchCollabsWith(identityKey: string, mututalIdentityKey: string) {
        return collabStore.fetchCollabsWith(identityKey, mututalIdentityKey).
            then(() => {
                return getCollabsWith(identityKey, mututalIdentityKey);
            });
    }

    function getCollabsCreatedBy(identityKey: string) {
        return collabStore.items.filter((c) => c.identity === identityKey);
    }

    async function supportFetchCollabs(workKey: string, lookup_identity: string, cursor?: string) {
        return collabStore.supportFetchCollabs(workKey, lookup_identity, cursor);
    }

    const data = {
        currentCollab: currentCollab,
        setCurrentCollabKey: setCurrentCollabKey,
        createCollab: createCollab,
        createCollabByDedicated: createCollabByDedicated,
        fetchCollab: fetchCollab,
        collabByKey: collabByKey,
        collabs: collabStore.items,
        fetchCollabsByProject: fetchCollabsByProject,
        getCollabsByProject: getCollabsByProject,
        fetchCollabsWith: fetchCollabsWith,
        getCollabsWith: getCollabsWith,
        getCollab: collabByKey,
        fetchCollabsByVariant: fetchCollabsByVariant,
        getCollabsByVariant: getCollabsByVariant,
        supportFetchCollabs: supportFetchCollabs,
        getCollabsCreatedBy: getCollabsCreatedBy,
    };

    return (
        <CollabsContext.Provider value={data}>
            { children }
        </CollabsContext.Provider>
    );

});
